import React, { useEffect, useState, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FakeText, Card, IconButton } from '@totalsoft/rocket-ui';
import Indicator from '@mui/icons-material/BarChart';
import { initialState, reducer } from '../../reducers/containerReducer';
import EngineAttributeComponent from './EngineAttributeComponent';
import { useValidation, isValid } from '@totalsoft/pure-validations-react';
import { engineAttributeValidator } from 'features/engineAttributes/validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { ENGINE_ATTRIBUTE_QUERY, SCORING_GRIDS_QUERY } from '../../queries';
import { ENGINE_ATTRIBUTE_SAVE, TEST_ENGINE_ATTRIBUTE } from '../../mutations';
import { ENGINE_ATTRIBUTE_SAVED, TEST_ENGINE_ATTRIBUTE_COMPLETED, TEST_ENGINE_ATTRIBUTE_FAILED } from '../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';

const EngineAttributeContainer = ({ id, onRedirect, onChangeTitle }) => {
  const { t } = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [attributeTestResult, setAttributeTestResult] = useState('');
  const client = useApolloClient();
  const [engineAttributeValidation, validateEngineAttribute] = useValidation(engineAttributeValidator);

  const { data: engineAttributeData, loading: engineAttributeLoading } = useQuery(ENGINE_ATTRIBUTE_QUERY, {
    variables: {
      engineAttributeId: id
    },
    skip: !id,
    fetchPolicy: 'network-only'
  });
  const engineAttribute = engineAttributeData?.engineAttribute;

  const { data: scoringGridsForAttributeTestingData, loading: scoringGridsLoading } = useQuery(SCORING_GRIDS_QUERY, {
    variables: {
      engineAttributeId: id
    }
  });
  const scoringGridsForAttributeTesting = scoringGridsForAttributeTestingData?.scoringGrids;

  const loading = engineAttributeLoading && scoringGridsLoading;

  const [engineAttributeSave] = useMutation(ENGINE_ATTRIBUTE_SAVE);
  const addToast = useToast();

  useSubscription(ENGINE_ATTRIBUTE_SAVED, {
    onData: data => {
      addToast(t('EngineAttributes.EngineAttributeSaved'), 'success');
      if (!id) onRedirect(id ?? data.data.data.engineAttributeUpdated.engineAttributeId);
      else
        client.cache.evict({
          id: `EngineAttribute:{"engineAttributeId":${id ?? data.data.data.engineAttributeUpdated.engineAttributeId}}`
        });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'engineAttributes' });
      client.cache.gc();
    }
  });

  const onSave = useCallback(
    data => {
      if (!validateEngineAttribute(localState.attribute)) return;
      dispatch({ type: 'OnSave' });
      engineAttributeSave({
        variables: {
          input: {
            id: id,
            orderNo: data.orderNo,
            attributeName: data.attributeName,
            description: data.description,
            expression: data.expression,
            sensitiveInfo: data.sensitiveInfo,
            storeYN: data.storeYN
          }
        }
      });
    },
    [engineAttributeSave, id, localState.attribute, validateEngineAttribute]
  );

  const [testEngineAttribute] = useMutation(TEST_ENGINE_ATTRIBUTE, {
    fetchPolicy: 'network-only'
  });

  useSubscription(TEST_ENGINE_ATTRIBUTE_COMPLETED, {
    onData: ({ data }) => {
      addToast(t('EngineAttributes.TestEngineAttributeCompleted'), 'success');
      setAttributeTestResult(data.data.testEngineAttributeCompleted.scoringGridTestResult);
    }
  });

  useSubscription(TEST_ENGINE_ATTRIBUTE_FAILED, {
    onData: ({ data }) => {
      addToast(data.data.testEngineAttributeFailed.errorMessage, 'error');
      setAttributeTestResult(data.data.testEngineAttributeFailed.errorMessage);
    }
  });

  const onTestAttribute = (engineAttributeId, engineScoringId, entityIdentifierForTest) => {
    if (!validateEngineAttribute(localState.attribute)) return;

    testEngineAttribute({ variables: { input: { engineAttributeId, engineScoringId, entityIdentifierForTest } } });
  };

  useEffect(() => {
    if (engineAttribute) {
      onChangeTitle(engineAttribute?.attributeName);
    }
  }, [engineAttribute, onChangeTitle]);

  useEffect(() => {
    if (!loading && engineAttribute) {
      if (id === undefined) {
        dispatch({ type: 'OnEngineAttributeAdd' });
      } else {
        dispatch({ type: 'OnInitState', engineAttribute });
      }
    }
  }, [engineAttribute, id, loading]);

  useEffect(() => {
    validateEngineAttribute(localState.attribute);
  }, [localState.attribute, validateEngineAttribute]);

  const onDetailSave = () => {
    onSave(localState.attribute);
  };

  return (
    <Card
      icon={Indicator}
      iconColor='primary'
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('EngineAttributes.SaveChanges')}
          onClick={onDetailSave}
          disabled={!localState.attribute.changed || !(engineAttributeValidation |> isValid)}
        />
      }
    >
      {loading || (id && localState === initialState) ? (
        <FakeText lines={10} />
      ) : !loading && (engineAttribute || !id) && scoringGridsForAttributeTesting ? (
        <EngineAttributeComponent
          engineAttribute={localState.attribute}
          dispatch={dispatch}
          validation={engineAttributeValidation}
          onTestAttribute={onTestAttribute}
          attributeTestResult={attributeTestResult}
          scoringGridsForAttributeTesting={scoringGridsForAttributeTesting}
        />
      ) : (
        <></>
      )}
    </Card>
  );
};

EngineAttributeContainer.propTypes = {
  id: PropTypes.number,
  onRedirect: PropTypes.func.isRequired,
  onChangeTitle: PropTypes.func.isRequired
};

export default EngineAttributeContainer;
